import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface ArticleSEOData {
  title: string;
  description: string;
  keywords: string;
  path: string;
  tags?: string[];
}

interface ArticleLayoutProps {
  title: string;
  subtitle: string;
  heroImage: string;
  heroImageAlt: string;
  breadcrumb: string;
  children: ReactNode;
  relatedArticles: { title: string; slug: string }[];
  ctaTitle: string;
  ctaSubtitle: string;
  seo?: ArticleSEOData;
}

export default function ArticleLayout({
  title,
  subtitle,
  heroImage,
  heroImageAlt,
  breadcrumb,
  children,
  relatedArticles,
  ctaTitle,
  ctaSubtitle,
  seo,
}: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoTitle = seo?.title || `${title} ${subtitle}`;
  const seoDescription = seo?.description || `Learn about ${title} ${subtitle} — a detailed Mulebuy Spreadsheet guide for cross-border shopping. Includes comparison tables, step-by-step workflows, and frequently asked questions. Updated June 2026.`;
  const seoKeywords = seo?.keywords || 'mulebuy spreadsheet, cross-border shopping, product sourcing guide';
  const seoPath = seo?.path || '';
  const seoTags = seo?.tags || ['mulebuy spreadsheet', 'cross-border shopping', 'guide'];

  return (
    <div className="min-h-screen bg-background-50">
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        path={seoPath}
        type="article"
        article={{
          publishedTime: '2026-06-01T00:00:00+00:00',
          modifiedTime: '2026-06-22T00:00:00+00:00',
          section: 'Shopping Guide',
          tags: seoTags,
        }}
      />
      <Navbar />

      <main>
        <section className="pt-20 md:pt-24 pb-8 md:pb-12 px-4 md:px-10">
          <div className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
            <Link to="/" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">Home</Link>
            <span className="text-foreground-300">/</span>
            <Link to="/what-is-mulebuy-spreadsheet" className="hover:text-primary-500 transition-colors duration-200 cursor-pointer">Guide</Link>
            <span className="text-foreground-300">/</span>
            <span className="text-foreground-400">{breadcrumb}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-primary-800 leading-[1.12] tracking-tight">
            {title}
            <br />
            <span className="text-foreground-950">{subtitle}</span>
          </h1>
          <p className="mt-4 text-sm text-foreground-500">Updated June 2026 &middot; Mulebuy Spreadsheet Guide Series</p>
        </section>

        {heroImage && (
          <section className="px-4 md:px-10 pb-8 md:pb-12">
            <div className="w-full h-[280px] md:h-[420px] rounded-2xl md:rounded-3xl overflow-hidden">
              <img
                src={heroImage}
                alt={heroImageAlt}
                title={heroImageAlt}
                className="w-full h-full object-cover object-top"
              />
            </div>
          </section>
        )}

        <section className="px-4 md:px-10 pb-16 md:pb-24">
          <div className="max-w-3xl">
            <article className="prose-custom">
              {children}
            </article>

            <div className="mt-16 pt-8 border-t border-background-200">
              <h3 className="text-sm font-semibold text-foreground-400 tracking-wider uppercase mb-4 font-label">Continue Reading</h3>
              <div className="flex flex-wrap gap-3">
                {relatedArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={article.slug}
                    className="px-5 py-2.5 rounded-full border border-background-200 bg-background-50 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    {article.title}
                  </Link>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row items-start gap-4">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="px-7 py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-all duration-200 whitespace-nowrap"
              >
                Visit Mulebuy Now
              </a>
              <Link
                to="/"
                className="px-7 py-3 rounded-full border border-primary-500/40 text-primary-700 text-sm font-medium cursor-pointer hover:bg-primary-50 transition-all duration-200 whitespace-nowrap"
              >
                Back to Homepage
              </Link>
            </div>

            <div className="mt-10 p-5 md:p-6 bg-accent-100/40 rounded-2xl border border-accent-200/30">
              <p className="text-sm text-foreground-600 leading-relaxed">
                <strong className="text-foreground-950">Disclaimer:</strong> This guide provides educational information about the Mulebuy Spreadsheet workflow system. We do not guarantee specific financial or business outcomes. Users should conduct their own due diligence before making purchasing decisions.
              </p>
            </div>
          </div>
        </section>

        <section className="relative w-full h-[350px] md:h-[440px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Wide%20panoramic%20shot%20of%20organized%20workspace%20with%20laptop%20open%20showing%20colorful%20spreadsheet%20data%20surrounded%20by%20product%20samples%20fabric%20swatches%20and%20shipping%20boxes%20on%20warm%20wooden%20desk%2C%20bright%20natural%20window%20light%2C%20productive%20ecommerce%20sourcing%20atmosphere%2C%20clean%20modern%20home%20office%2C%20professional%20yet%20approachable%20aesthetic&width=1800&height=880&seq=article-bottom-cta-v2&orientation=landscape"
            alt="Start your Mulebuy Spreadsheet journey"
            title="Begin using Mulebuy Spreadsheet today"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/15 to-black/30"></div>
          <div className="relative w-full h-full px-6 md:px-14 flex flex-col justify-between py-12 md:py-16">
            <div className="max-w-xl">
              <h2 className="font-heading text-4xl md:text-6xl font-bold text-background-50 leading-[1.08] tracking-tight lowercase">
                {ctaTitle}
              </h2>
            </div>
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-6">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-background-50/90 backdrop-blur-sm text-foreground-950 text-sm font-medium cursor-pointer hover:bg-background-50 transition-all duration-200 whitespace-nowrap group"
              >
                <span>Explore Now</span>
                <span className="w-8 h-8 bg-foreground-950 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                  <i className="ri-arrow-right-line text-background-50 text-sm"></i>
                </span>
              </a>
              <div className="text-right max-w-sm">
                <p className="text-lg text-background-50/90 leading-relaxed font-light">
                  {ctaSubtitle}
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}